// Landing Page Component - Welcome screen for new users
import React from 'react';
import { 
  Sparkles, BookOpen, Zap, Users, Target, Compass, ArrowRight, Star, Brain, Clock, TrendingUp, Lock, Trophy, Shield, AlertCircle, Flame, CheckCircle, Code, Cpu, Lightbulb, Rocket, Mail, Send
} from 'lucide-react';
import { Logo } from '../Logo';
import { HeroIllustration } from './HeroIllustration';
import { saveContactMessage } from '../../services/contactService';

interface LandingPageProps {
  theme: 'light' | 'dark';
  onGetStarted: () => void;
  onNavigateToContact?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  theme,
  onGetStarted,
  onNavigateToContact
}) => {
  const isDark = theme === 'dark';

  const features = [
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "AI-Powered Roadmaps",
      description: "Advanced algorithms create personalized learning paths tailored to your specific goals and learning pace"
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Curated Resources",
      description: "Vetted learning materials from the best platforms, eliminating decision paralysis"
    },
    {
      icon: <Flame className="w-6 h-6" />,
      title: "Streak Psychology",
      description: "Build unstoppable learning momentum with daily streaks that protect your progress"
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Nano Learning",
      description: "Bite-sized lessons designed for your brain's optimal capacity - no overwhelm"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Progress Visualization",
      description: "See exactly where you are, where you're going, and how far you've come"
    },
    {
      icon: <Compass className="w-6 h-6" />,
      title: "Multi-Language Support",
      description: "Learn in your native language with support for 8+ languages"
    }
  ];

  const problems = [
    {
      icon: <AlertCircle className="w-6 h-6" />,
      problem: "Overwhelm Kills Your Identity",
      solution: "Traditional courses throw everything at you. NanoLez breaks learning into nano-pieces your brain can actually process."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      problem: "You Start, Then Abandon",
      solution: "Most learners quit within 2 weeks. Our streak system builds psychological momentum to keep you going."
    },
    {
      icon: <Target className="w-6 h-6" />,
      problem: "No Clear Direction",
      solution: "Without a roadmap, you waste months going in circles. AI-powered paths eliminate guessing and keep you focused."
    },
    {
      icon: <Lock className="w-6 h-6" />,
      problem: "Scattered Resources Trap You",
      solution: "Too many options paralyze you. We curate THE best resources so you stop researching and start learning."
    }
  ];

  const differences = [
    {
      title: "vs. Traditional Online Courses",
      features: [
        "3-6 month courses → Nano roadmaps in days",
        "One-size-fits-all → Personalized AI paths",
        "Passive watching → Active learning with checkpoints",
        "High dropout → Streak protection keeps you engaged"
      ]
    },
    {
      title: "vs. Free Learning Platforms",
      features: [
        "Random resources → Curated expert selections",
        "No guidance → AI-generated roadmaps",
        "No accountability → Daily streaks & progress tracking",
        "Overwhelming choices → Clear learning path"
      ]
    },
    {
      title: "vs. Generic Learning Apps",
      features: [
        "Generic topics → ANY skill you want to master",
        "Slow progress → Fast momentum with streaks",
        "No personalization → AI learns your pace",
        "No community → Shared roadmaps & insights"
      ]
    }
  ];

  const testimonials = [
    {
      name: "Alex Chen",
      role: "Learned Python in 30 Days",
      text: "I tried 3 different courses and gave up each time. NanoLez's roadmap kept me on track. The daily streaks made it feel like a game I couldn't lose.",
      stat: "From 0 to building apps"
    },
    {
      name: "Sarah Williams",
      role: "Mastered UI Design",
      text: "The nano-lessons are perfect. I can learn in 15 minutes before work. Finally building a design portfolio without feeling overwhelmed.",
      stat: "Portfolio completed in 60 days"
    },
    {
      name: "Marcus Johnson",
      role: "Career Switcher",
      text: "The AI roadmap saved me months of research. Instead of guessing what to learn, I had a crystal-clear path from day one.",
      stat: "Career transition completed"
    }
  ];

  const stats = [
    { label: "Active Learners", value: "10K+" },
    { label: "Skills Mastered", value: "5K+" },
    { label: "Days Average Streak", value: "28" }
  ];

  return (
    <div className="space-y-24 animate-in">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-8">
        {/* Gradient Background */}
        <div className="absolute inset-0 -z-10">
          <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-20 ${
            isDark ? 'bg-red-500' : 'bg-red-300'
          }`} />
          <div className={`absolute top-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl opacity-15 ${
            isDark ? 'bg-rose-500' : 'bg-rose-200'
          }`} />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-500/10 to-rose-500/10 border border-red-500/20">
            <Flame className="w-4 h-4 text-red-500" />
            <span className="text-sm font-medium text-red-500">🚀 The AI Learning Platform That Actually Works</span>
          </div>

          {/* Main Heading */}
          <div className="space-y-4">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.1]">
              <span className="bg-gradient-to-r from-red-600 via-rose-600 to-orange-600 bg-clip-text text-transparent">
                Master Any Skill
              </span>
              <br />
              <span className={isDark ? 'text-white' : 'text-zinc-900'}>
                Without Overwhelm
              </span>
            </h1>
            
            <p className={`text-xl sm:text-2xl max-w-3xl mx-auto leading-relaxed ${
              isDark ? 'text-zinc-400' : 'text-zinc-600'
            }`}>
              AI-powered learning roadmaps + daily streaks + psychological momentum = Results. 
              Stop wandering. Start winning.
            </p>
          </div>

          {/* Hero Illustration */}
          <div className="py-8 px-4">
            <HeroIllustration isDark={isDark} />
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            <button
              onClick={onGetStarted}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-2xl font-bold text-lg hover:opacity-90 transition-all shadow-lg shadow-red-600/40 hover:shadow-xl hover:scale-105 active:scale-95"
            >
              Create Your First Roadmap
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Stats - Learning Environment Style */}
          <div className="grid grid-cols-3 gap-4 sm:gap-8 pt-12 border-t border-white/10">
            {stats.map((stat, idx) => (
              <div key={stat.label} className={`space-y-2 p-4 rounded-xl transition-all ${
                isDark ? 'hover:bg-white/5' : 'hover:bg-zinc-50'
              }`}>
                {/* Small decorative badge */}
                <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full mx-auto mb-2 ${
                  idx === 0 
                    ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
                    : idx === 1
                    ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                    : isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
                }`}>
                  {idx === 0 ? '👥' : idx === 1 ? '🎯' : '🔥'}
                </div>
                <p className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-red-500 to-rose-500 bg-clip-text text-transparent">
                  {stat.value}
                </p>
                <p className={`text-sm sm:text-base font-medium ${
                  isDark ? 'text-zinc-400' : 'text-zinc-500'
                }`}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="space-y-12">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-500/10 border border-red-500/20">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-sm font-medium text-red-600 dark:text-red-400">The Reality Every Learner Faces</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
              Why You Keep Failing
            </span>
            <br />
            <span className={isDark ? 'text-white' : 'text-zinc-900'}>
              (And It's Not Your Fault)
            </span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDark ? 'text-zinc-400' : 'text-zinc-600'
          }`}>
            Every learner hits the same walls. NanoLez was built to break through them.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {problems.map((item, idx) => (
            <div
              key={idx}
              className={`group rounded-2xl border p-8 transition-all hover:shadow-lg relative overflow-hidden ${
                isDark
                  ? 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06]'
                  : 'bg-white border-zinc-200 hover:border-zinc-300 shadow-sm'
              }`}
            >
              {/* Learning background pattern */}
              <div className={`absolute top-0 right-0 w-20 h-20 opacity-5 pointer-events-none ${
                isDark ? 'text-red-400' : 'text-red-200'
              }`}>
                <BookOpen className="w-full h-full" />
              </div>
              
              <div className="mb-4 flex items-start gap-4 relative z-10">
                <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                  idx === 0 
                    ? isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
                    : idx === 1
                    ? isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'
                    : idx === 2
                    ? isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
                    : isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                }`}>
                  {item.icon}
                </div>
                <div>
                  <h3 className={`text-lg font-bold mb-2 ${
                    isDark ? 'text-white' : 'text-zinc-900'
                  }`}>
                    {item.problem}
                  </h3>
                  <p className={`text-sm leading-relaxed ${
                    isDark ? 'text-zinc-400' : 'text-zinc-600'
                  }`}>
                    {item.solution}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="space-y-12">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20">
            <Sparkles className="w-5 h-5 text-green-500" />
            <span className="text-sm font-medium text-green-600 dark:text-green-400">Powerful Features</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
              Everything Built For
            </span>
            <br />
            <span className={isDark ? 'text-white' : 'text-zinc-900'}>
              Learning Success
            </span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDark ? 'text-zinc-400' : 'text-zinc-600'
          }`}>
            A complete system designed around how humans actually learn
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, idx) => (
            <div
              key={idx}
              className={`group rounded-2xl border p-8 transition-all hover:shadow-lg ${
                isDark
                  ? 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06]'
                  : 'bg-white border-zinc-200 hover:border-zinc-300 shadow-sm hover:shadow-lg'
              }`}
            >
              <div className="mb-4 inline-flex p-3 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 text-green-500 group-hover:scale-110 transition-transform">
                {feature.icon}
              </div>
              <h3 className={`text-lg font-bold mb-2 ${
                isDark ? 'text-white' : 'text-zinc-900'
              }`}>
                {feature.title}
              </h3>
              <p className={`text-sm leading-relaxed ${
                isDark ? 'text-zinc-400' : 'text-zinc-600'
              }`}>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="space-y-12">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20">
            <Rocket className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Simple Process</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
              How It Works
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              step: "1",
              title: "Define Your Goal",
              description: "Tell us what skill you want to master and your target date. Any topic, any timeline.",
              icon: "🎯"
            },
            {
              step: "2",
              title: "AI Generates Your Path",
              description: "Our advanced AI creates a personalized roadmap with daily nano-lessons designed for your pace.",
              icon: "🤖"
            },
            {
              step: "3",
              title: "Build Your Streak",
              description: "Follow the roadmap daily. Each day you complete builds an unstoppable momentum streak.",
              icon: "🔥"
            }
          ].map((item, idx) => (
            <div key={item.step} className={`relative text-center space-y-4 p-8 rounded-2xl border transition-all hover:shadow-lg ${
              isDark ? 'bg-white/[0.03] border-white/10 hover:bg-white/5' : 'bg-white border-zinc-200 shadow-sm hover:shadow-md'
            }`}>
              {/* Learning milestone decoration */}
              <div className="absolute -top-3 right-4">
                <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                  idx === 0 
                    ? isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-600'
                    : idx === 1
                    ? isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600'
                    : isDark ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-600'
                }`}>
                  {idx === 0 ? 'Step 1' : idx === 1 ? 'Step 2' : 'Step 3'}
                </div>
              </div>
              
              <div className="text-5xl">{item.icon}</div>
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-black text-2xl shadow-lg shadow-blue-600/40 mx-auto">
                {item.step}
              </div>
              <h3 className={`text-xl font-bold ${
                isDark ? 'text-white' : 'text-zinc-900'
              }`}>
                {item.title}
              </h3>
              <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Why NanoLez is Different */}
      <section className="space-y-12">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20">
            <Shield className="w-5 h-5 text-purple-500" />
            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">What Sets Us Apart</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Why NanoLez Wins
            </span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {differences.map((comp, idx) => (
            <div
              key={idx}
              className={`rounded-2xl border p-8 ${
                isDark
                  ? 'bg-white/[0.03] border-white/10'
                  : 'bg-white border-zinc-200 shadow-sm'
              }`}
            >
              <h3 className={`text-lg font-bold mb-6 ${
                isDark ? 'text-white' : 'text-zinc-900'
              }`}>
                {comp.title}
              </h3>
              <ul className="space-y-3">
                {comp.features.map((feature, fidx) => (
                  <li key={fidx} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className={`text-sm ${
                      isDark ? 'text-zinc-400' : 'text-zinc-600'
                    }`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof / Testimonials */}
      <section className="space-y-12">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Real Results</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
              Success Stories
            </span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDark ? 'text-zinc-400' : 'text-zinc-600'
          }`}>
            See how real learners transformed their skills with NanoLez
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, idx) => (
            <div
              key={idx}
              className={`rounded-2xl border p-8 space-y-4 relative overflow-hidden transition-all hover:shadow-lg ${
                isDark
                  ? 'bg-white/[0.03] border-white/10 hover:bg-white/5'
                  : 'bg-white border-zinc-200 shadow-sm hover:shadow-md'
              }`}
            >
              {/* Learning achievement badge */}
              <div className={`absolute -top-2 -right-2 w-14 h-14 rounded-full flex items-center justify-center text-xl font-black ${
                idx === 0 
                  ? isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                  : idx === 1
                  ? isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                  : isDark ? 'bg-green-500/20' : 'bg-green-100'
              }`}>
                {idx === 0 ? '🐍' : idx === 1 ? '🎨' : '🚀'}
              </div>

              <div className="flex gap-1 relative z-10">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                ))}
              </div>
              <p className={`text-base leading-relaxed relative z-10 ${
                isDark ? 'text-zinc-300' : 'text-zinc-700'
              }`}>
                "{testimonial.text}"
              </p>
              <div className="pt-4 border-t border-white/10 relative z-10">
                <p className={`font-bold ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                  {testimonial.name}
                </p>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  {testimonial.role}
                </p>
                <p className="text-sm font-medium bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent mt-2">
                  ✓ {testimonial.stat}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* The Science Section */}
      <section className={`rounded-3xl border p-12 md:p-16 space-y-8 ${
        isDark
          ? 'bg-gradient-to-br from-blue-600/10 via-indigo-600/10 to-purple-600/10 border-blue-500/20'
          : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
      }`}>
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20">
            <Brain className="w-5 h-5 text-blue-500" />
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Science-Backed</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Built on Psychology & Neuroscience
            </span>
          </h2>
          <p className={`text-lg max-w-3xl mx-auto ${
            isDark ? 'text-zinc-400' : 'text-zinc-600'
          }`}>
            Every feature is designed based on how your brain actually learns. No busy work. No overwhelm. Just pure learning momentum.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Spaced Repetition
            </h3>
            <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
              Lessons are spaced to hit your brain's optimal learning window
            </p>
          </div>
          <div className="space-y-2">
            <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Flame className="w-5 h-5 text-red-500" />
              Streak Psychology
            </h3>
            <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
              Streaks tap into loss aversion - your mind protects what it's built
            </p>
          </div>
          <div className="space-y-2">
            <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <Target className="w-5 h-5 text-green-500" />
              Micro-Commitments
            </h3>
            <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
              Nano-lessons make starting easy - you're more likely to keep going
            </p>
          </div>
          <div className="space-y-2">
            <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-zinc-900'}`}>
              <TrendingUp className="w-5 h-5 text-blue-500" />
              Momentum Building
            </h3>
            <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>
              Each day you complete strengthens your identity as a learner
            </p>
          </div>
        </div>
      </section>

      {/* Get in Touch / Contact Form Section */}
      <section className="space-y-12">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/20">
            <Mail className="w-5 h-5 text-pink-500" />
            <span className="text-sm font-medium text-pink-600 dark:text-pink-400">Get in Touch</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            <span className="bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
              Have Questions?
            </span>
            <br />
            <span className={isDark ? 'text-white' : 'text-zinc-900'}>
              We're Here to Help
            </span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDark ? 'text-zinc-400' : 'text-zinc-600'
          }`}>
            Send us your message and we'll get back to you within 24 hours
          </p>
        </div>

        <div className={`rounded-2xl border p-8 sm:p-12 max-w-2xl mx-auto ${
          isDark
            ? 'bg-white/[0.03] border-white/10'
            : 'bg-white border-zinc-200 shadow-sm'
        }`}>
          <ContactFormSection isDark={isDark} onNavigateToContact={onNavigateToContact} />
        </div>
      </section>

      {/* Final CTA Section */}
      <section className={`rounded-3xl border p-12 md:p-16 text-center space-y-6 ${
        isDark
          ? 'bg-gradient-to-br from-red-600/10 via-rose-600/10 to-orange-600/10 border-red-500/20'
          : 'bg-gradient-to-br from-red-50 to-rose-50 border-red-200'
      }`}>
        <div className="space-y-3">
          <h2 className="text-3xl sm:text-4xl font-black">
            <span className="bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent">
              Stop Spinning Your Wheels
            </span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${
            isDark ? 'text-zinc-400' : 'text-zinc-600'
          }`}>
            The roadmap is ready. Your AI tutor is waiting. All that's left is taking the first step.
          </p>
        </div>
        <button
          onClick={onGetStarted}
          className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-2xl font-bold text-lg hover:opacity-90 transition-all shadow-lg shadow-red-600/40 hover:shadow-xl hover:scale-105 active:scale-95"
        >
          Create Your First Roadmap (Free)
          <ArrowRight className="w-5 h-5" />
        </button>
      </section>
    </div>
  );
};

// Contact Form Component for Landing Page
const ContactFormSection: React.FC<{ isDark: boolean; onNavigateToContact?: () => void }> = ({ isDark, onNavigateToContact }) => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    purpose: 'General Inquiry',
    message: ''
  });
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const purposes = ['Bug Report', 'Feature Request', 'General Inquiry', 'Partnership', 'Feedback'];

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email');
      return;
    }
    if (!formData.message.trim() || formData.message.length < 10) {
      setError('Message must be at least 10 characters');
      return;
    }

    setLoading(true);
    
    // Defer state updates to after form is disconnected
    const submitMessage = async () => {
      try {
        console.log('📤 Submitting message...', formData);
        
        // Save to Supabase and localStorage via service
        const newMessage = await saveContactMessage({
          name: formData.name,
          email: formData.email,
          purpose: formData.purpose,
          message: formData.message
        });

        if (newMessage) {
          console.log('✅ Message submitted successfully:', newMessage);
          
          // Dispatch custom event to notify admin inbox of new message
          window.dispatchEvent(new CustomEvent('contact_message_sent', { detail: newMessage }));

          setSubmitted(true);
          setFormData({ name: '', email: '', purpose: 'General Inquiry', message: '' });
          setError('');
          setTimeout(() => setSubmitted(false), 5000);
        } else {
          console.error('Failed to save message - service returned null');
          setError('Failed to save message. Please try again.');
        }
      } catch (err) {
        console.error('❌ Form submission error:', err);
        setError('Failed to send message. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    // Run after form is disconnected
    requestAnimationFrame(() => submitMessage());
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          type="text"
          name="name"
          placeholder="Your Name"
          value={formData.name}
          onChange={handleChange}
          disabled={loading}
          className={`px-4 py-3 rounded-lg border transition-colors text-sm ${
            isDark
              ? 'bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 focus:border-red-500 focus:outline-none'
              : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-500 focus:border-red-500 focus:outline-none'
          }`}
        />
        <input
          type="email"
          name="email"
          placeholder="Your Email"
          value={formData.email}
          onChange={handleChange}
          disabled={loading}
          className={`px-4 py-3 rounded-lg border transition-colors text-sm ${
            isDark
              ? 'bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 focus:border-red-500 focus:outline-none'
              : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-500 focus:border-red-500 focus:outline-none'
          }`}
        />
      </div>

      <select
        name="purpose"
        value={formData.purpose}
        onChange={handleChange}
        disabled={loading}
        className={`w-full px-4 py-3 rounded-lg border transition-colors text-sm ${
          isDark
            ? 'bg-zinc-900 border-zinc-800 text-white focus:border-red-500 focus:outline-none'
            : 'bg-white border-zinc-300 text-zinc-900 focus:border-red-500 focus:outline-none'
        }`}
      >
        {purposes.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>

      <textarea
        name="message"
        placeholder="Your message (minimum 10 characters)..."
        value={formData.message}
        onChange={handleChange}
        disabled={loading}
        rows={3}
        className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none text-sm ${
          isDark
            ? 'bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 focus:border-red-500 focus:outline-none'
            : 'bg-white border-zinc-300 text-zinc-900 placeholder-zinc-500 focus:border-red-500 focus:outline-none'
        }`}
      />

      {error && (
        <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
          isDark
            ? 'bg-red-500/20 border border-red-500/50 text-red-300'
            : 'bg-red-100 border border-red-300 text-red-700'
        }`}>
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {submitted && (
        <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
          isDark
            ? 'bg-green-500/20 border border-green-500/50 text-green-300'
            : 'bg-green-100 border border-green-300 text-green-700'
        }`}>
          ✓ Message sent! We'll reply within 24 hours.
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-pink-600/25"
      >
        <Send className="w-4 h-4" />
        {loading ? 'Sending...' : 'Send Message'}
      </button>

      <p className={`text-xs text-center ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>
        For detailed inquiries, visit our <button onClick={onNavigateToContact} className="text-red-500 hover:text-red-600 font-semibold bg-transparent border-none cursor-pointer">full contact page</button>
      </p>
    </form>
  );
};
